/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx, css } from "@emotion/core"
import { FC, Fragment } from "react"
import { Button, Row, Col } from "@duxton-web/ui"
import { get, isEmpty } from "lodash"
import { ServiceHours, ServiceDay } from "types/api"
import {
  days,
  openPeriodTypes,
  OpenPeriodText,
  DaysText,
} from "utils/section_helper"
import { useTranslation, useTranslationsMap } from "common/utils/i18n"
import { drawerContentStyle } from "../styles"

const { OPEN_ALL_DAY, CLOSED_ALL_DAY } = openPeriodTypes

export interface ServiceHoursListProps {
  serviceHours: ServiceHours
  disabledEdit: boolean
  onClickEditTimings(e: any): void
}

const renderTimes = (
  serviceDay: ServiceDay,
  openPeriodText: { [key: string]: string },
) => {
  if (serviceDay.openPeriodType === CLOSED_ALL_DAY)
    return <span>{openPeriodText[CLOSED_ALL_DAY]}</span>

  const periods =
    serviceDay.openPeriodType === OPEN_ALL_DAY
      ? [{ startTime: "00:00", endTime: "24:00" }]
      : serviceDay?.periods

  return (
    <span>
      {periods
        ?.map(({ startTime, endTime }) => `${startTime} - ${endTime}`)
        .join(", ")}
    </span>
  )
}

export const ServiceHoursList: FC<ServiceHoursListProps> = ({
  serviceHours,
  disabledEdit,
  onClickEditTimings,
}: ServiceHoursListProps) => {
  const textMap = useTranslationsMap({
    edit: "menu.actions.edit",
    setTimings: "menu.drawers.section.buttons.setTimings",
    timingPlaceholder: "menu.drawers.section.placeholders.timing",
  })

  const daysText = DaysText()
  const openPeriodText = OpenPeriodText()

  return (
    <Fragment>
      <div
        className="dui-drawer-title"
        css={css`
          padding: 24px;
        `}
      >
        <Row align="middle" justify="space-between">
          <Col
            css={css`
              font-size: 16px;
            `}
          >
            {useTranslation("menu.drawers.section.timing")}
          </Col>
          <Col>
            {!isEmpty(serviceHours) && !disabledEdit && (
              <Button
                type="link"
                disabled={disabledEdit}
                onClick={onClickEditTimings}
              >
                {textMap.edit}
              </Button>
            )}
          </Col>
        </Row>
      </div>
      <Fragment>
        {!isEmpty(serviceHours) ? (
          <div css={drawerContentStyle}>
            {days.map(day => {
              const serviceHour = get(
                serviceHours,
                day.toLowerCase(),
              ) as ServiceDay
              return (
                <Row
                  key={day}
                  align="middle"
                  css={css`
                    line-height: 1.7;
                  `}
                >
                  <Col
                    span={3}
                    css={css`
                      font-weight: 500;
                    `}
                  >
                    {daysText[day]}
                  </Col>
                  <Col>{renderTimes(serviceHour, openPeriodText)}</Col>
                </Row>
              )
            })}
          </div>
        ) : (
          <div
            css={css`
              ${drawerContentStyle}
              padding: 24px 62px;
              text-align: center;
            `}
          >
            <p>{textMap.timingPlaceholder}</p>
            <Button disabled={disabledEdit} onClick={onClickEditTimings}>
              {textMap.setTimings}
            </Button>
          </div>
        )}
      </Fragment>
    </Fragment>
  )
}
